import { useCallback } from 'react';
import { rage, useObservable } from '~/rage';
import {
	HandlingMode,
	IModalReplyDefault,
	ModalButtonColor,
	ModalType,
} from '~/shared';
import {
	setModeValue,
	setUnsavedChanges,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';

const useModal = () => {
	const dispatch = useLayoutDispatch();
	const saveAbsoluteIndex = useLayoutSelector(
		(state) => state.handlingList.saveAbsoluteIndex,
	);
	const presetList = useLayoutSelector(
		(state) => state.handlingList.savePresetList,
	);
	const unsavedChanges = useLayoutSelector(
		(state) => state.handling.unsavedChanges,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const handleSetupNewPresetModal = useCallback(() => {
		if (modalState) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: 'Вы действительно хотите сбросить свои настройки и установить новые?',
				acceptButton: { text: 'Установить', color: ModalButtonColor.Primary },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.applyHandling();
					dispatch(setUnsavedChanges(false));
				}
			},
		);
	}, [modalState]);

	const handleSetupSaveModal = useCallback(() => {
		if (modalState) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите сбросить свои настройки и установить “${presetList[saveAbsoluteIndex].name}”?`,
				acceptButton: { text: 'Установить', color: ModalButtonColor.Primary },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.applyPreset(saveAbsoluteIndex);
					dispatch(setUnsavedChanges(false));
				}
			},
		);
	}, [saveAbsoluteIndex, presetList, modalState]);

	const handleDeleteModal = useCallback(() => {
		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите удалить настройку "${presetList?.[saveAbsoluteIndex]?.name}"?`,
				acceptButton: { text: 'Удалить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.removePresetByIndex(saveAbsoluteIndex);
				}
			},
		);
	}, [presetList, saveAbsoluteIndex]);

	const handleResetModal = useCallback((name: string, resetId: string) => {
		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите сбросить настройку "${name}"?`,
				acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					dispatch(setUnsavedChanges(true));
					rage.handlingTuning.resetPropertyHandling(resetId);
				}
			},
		);
	}, []);

	const handleResetAllModal = useCallback(() => {
		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: 'Вы действительно хотите сбросить все настройки?',
				acceptButton: { text: 'Сбросить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					dispatch(setUnsavedChanges(true));
					rage.handlingTuning.resetAllSectionHandling();
				}
			},
		);
	}, []);

	const handleUnsavedChangesExitModal = useCallback(() => {
		if (modalState) {
			return;
		}

		if (!unsavedChanges) {
			rage.handlingTuning.exitMenu();
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: 'У вас имеются несохраненные настройки, вы действительно хотите выйти?',
				acceptButton: { text: 'Выйти', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) {
					rage.handlingTuning.exitMenu();
				}
			},
		);
	}, [unsavedChanges, modalState]);

	const handleUnsavedChangesModeModal = useCallback(
		(mode: HandlingMode) => {
			if (!unsavedChanges) {
				rage.vehicles.setLocalVehicleHandlingMode(mode);
				dispatch(setModeValue(mode));
				return;
			}

			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: 'У вас имеются несохраненные настройки, вы действительно хотите сменить режим?',
					acceptButton: { text: 'Сменить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				({ isAccepted }: IModalReplyDefault) => {
					if (isAccepted) {
						rage.vehicles.setLocalVehicleHandlingMode(mode);
						dispatch(setModeValue(mode));
					}
				},
			);
		},
		[unsavedChanges],
	);

	return {
		handleSetupNewPresetModal,
		handleSetupSaveModal,
		handleDeleteModal,
		handleResetModal,
		handleResetAllModal,
		handleUnsavedChangesExitModal,
		handleUnsavedChangesModeModal,
	};
};

export { useModal };
